import { useCallback, useEffect, useState } from "react";
import useFetchWithMsal from "../../../useFetchWithMsal";
import { thor_api_urls } from "../../common/Constants";
import { buildDataState, buildLoadingState } from "../../../utils/StateUtils";
import { useThor } from "../context/ThorContext";
import { getValidTransitions, getStateLabel, getTransitionRequirement, REVIEW_TYPES } from "../utils/thorState";
import StateHandler from "../../common/elements/StateHandler";
import SmallLoadingSpinner from "../../common/elements/SmallLoadingSpinner";
import StateChip from "./StateChip";
import Button from "../../common/elements/Button";
import { serverErrMsg } from "../utils/validators";
import { humanizeMessage, getTabForMessage } from "./ValidationChip";

// ── Lifecycle path (main branch only — paused/cancelled are side branches) ───
const LIFECYCLE_MAIN_PATH = [
    'design', 'design_review', 'instrumentation', 'pre_prod_validation',
    'running', 'ended', 'cleanup', 'finished',
];

// ── Transition button tooltips ───────────────────────────────────────────────
const TRANSITION_TOOLTIPS = {
    'design->design_review': 'Requires: experiment validation to pass (check the issues chip)',
    'design_review->instrumentation': 'Requires: Analyst Design Review and Engineer Design Review approvals completed',
    'design_review->design': 'Send back to Design for revisions',
    'instrumentation->pre_prod_validation': 'Requires: Engineer Instrumentation Signoff approval completed',
    'instrumentation->design': 'Send back to Design',
    'pre_prod_validation->running': 'Requires: Owner Pre-prod Signoff approval completed',
    'running->ended': 'Requires: transition reason (optional) and outcome after',
    'running->paused': 'Pause the experiment in all environments',
    'paused->running': 'Resume the experiment',
    'paused->ended': 'End the experiment without resuming',
    'ended->cleanup': 'Requires: Outcome to be filled in',
    'cleanup->finished': 'Requires: Engineer Cleanup Signoff approval completed',
    'design->cancelled': 'Cancel this experiment permanently',
    'finished->paused': 'Re-open this experiment (e.g. to revert a shipped variant)',
};

// ── Transition modal ─────────────────────────────────────────────────────────

const TransitionModal = ({ isActive, targetState, onClose, onConfirm, isSubmitting }) => {
    const [reason, setReason] = useState('');
    const [skipValidations, setSkipValidations] = useState(false);

    useEffect(() => { if (!isActive) { setReason(''); setSkipValidations(false); } }, [isActive]);

    return (
        <div className={`modal ${isActive ? 'is-active' : ''}`}>
            <div className="modal-background" onClick={onClose} />
            <div className="modal-card">
                <header className="modal-card-head">
                    <p className="modal-card-title">Move to {getStateLabel(targetState)}</p>
                    <button className="delete" onClick={onClose} />
                </header>
                <section className="modal-card-body">
                    <div className="field">
                        <label className="label">Reason <span className="has-text-grey is-size-7">(optional)</span></label>
                        <textarea className="textarea is-small" rows={2} value={reason}
                                  onChange={e => setReason(e.target.value)} placeholder="Why is this transition being made?" />
                    </div>
                    {targetState !== 'cancelled' && (
                        <div className="field mt-3">
                            <div className="has-background-warning-light p-2 mt-2" style={{ borderRadius: '4px' }}>
                                <label className="checkbox">
                                    <input type="checkbox" checked={skipValidations}
                                           onChange={e => setSkipValidations(e.target.checked)} />
                                    {' '}Skip validations
                                </label>
                                <p className="help has-text-grey">
                                    Skipping validations bypasses server-side checks for this transition.
                                    Follow experimentation best practices.
                                </p>
                            </div>
                        </div>
                    )}
                </section>
                <footer className="modal-card-foot">
                    <Button className="is-link mr-2"
                            onClick={() => onConfirm({ targetState, transitionReason: reason, isSkipValidations: skipValidations })}
                            disabled={isSubmitting}>
                        {isSubmitting ? 'Moving…' : `Move to ${getStateLabel(targetState)}`}
                    </Button>
                    <Button onClick={onClose}>Cancel</Button>
                </footer>
            </div>
        </div>
    );
};

// ── Start PROD confirmation modal ────────────────────────────────────────────

const StartProdModal = ({ isActive, experimentKey, onClose, onConfirm, isSubmitting }) => {
    const { execute } = useFetchWithMsal();
    const [classification, setClassification] = useState(null);
    const [loadingClass, setLoadingClass] = useState(false);

    useEffect(() => {
        if (!isActive) return;
        setLoadingClass(true);
        execute('GET', thor_api_urls.experimentClassification(experimentKey))
            .then(data => setClassification(data))
            .catch(() => setClassification(null))
            .finally(() => setLoadingClass(false));
    }, [isActive, experimentKey, execute]);

    const complexityColor = {
        LOW: 'is-success is-light',
        MEDIUM: 'is-warning is-light',
        HIGH: 'is-danger is-light',
    };

    return (
        <div className={`modal ${isActive ? 'is-active' : ''}`}>
            <div className="modal-background" onClick={onClose} />
            <div className="modal-card">
                <header className="modal-card-head" style={{ background: '#fff3cd', borderBottom: '1px solid #ffc107' }}>
                    <p className="modal-card-title">⚠️ Start in Production</p>
                    <button className="delete" onClick={onClose} />
                </header>
                <section className="modal-card-body">
                    <div className="notification is-warning is-light mb-4">
                        You are about to start this experiment in <strong>PRODUCTION</strong>. This will affect real users. Please review before confirming.
                    </div>
                    {loadingClass
                        ? <SmallLoadingSpinner />
                        : classification?.complexity && (
                            <div className="mb-3">
                                <p className="label is-small mb-1">Experiment Complexity</p>
                                <span className={`tag is-medium ${complexityColor[classification.complexity] ?? 'is-light'}`}>
                                    {classification.complexity}
                                </span>
                                {classification.complexity === 'HIGH' && (
                                    <p className="help is-danger mt-1">High complexity experiments carry elevated risk. Ensure all reviews and validations are complete.</p>
                                )}
                            </div>
                        )}
                    <p className="has-text-grey is-size-7">Once started in production, the experiment will begin bucketing real users. You can pause it at any time from this page.</p>
                </section>
                <footer className="modal-card-foot">
                    <Button className="is-danger mr-2" onClick={onConfirm} disabled={isSubmitting}>
                        {isSubmitting ? 'Starting…' : 'Yes, Start in Production'}
                    </Button>
                    <Button onClick={onClose}>Cancel</Button>
                </footer>
            </div>
        </div>
    );
};

// ── Fix 3 — Bypass approvals warning modal ───────────────────────────────────

const BypassApprovalModal = ({ isActive, isProd, pendingApprovals, onClose, onStartAnyway }) => {
    return (
        <div className={`modal ${isActive ? 'is-active' : ''}`}>
            <div className="modal-background" onClick={onClose} />
            <div className="modal-card">
                <header className={`modal-card-head ${isProd ? 'has-background-danger-light' : 'has-background-warning-light'}`}
                        style={{ borderBottom: isProd ? '1px solid #f14668' : '1px solid #ffc107' }}>
                    <p className={`modal-card-title ${isProd ? 'has-text-danger' : ''}`}>
                        Start without completing approvals?
                    </p>
                    <button className="delete" onClick={onClose} />
                </header>
                <section className="modal-card-body">
                    {isProd
                        ? (
                            <div className="notification is-danger is-light mb-3">
                                <strong>Starting production without approvals is not recommended.</strong>
                                {' '}This will affect real users with unreviewed experiment configuration.
                            </div>
                        )
                        : (
                            <div className="notification is-warning is-light mb-3">
                                We recommend completing all required approvals first for the best experiment quality and data integrity.
                                You can still start, but skipping approvals may affect experiment validity.
                            </div>
                        )
                    }
                    {pendingApprovals.length > 0 && (
                        <div>
                            <p className="has-text-weight-semibold is-size-7 mb-1">Pending approvals:</p>
                            <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem' }}>
                                {pendingApprovals.map(a => (
                                    <li key={a} className="is-size-7 has-text-grey">{a}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </section>
                <footer className="modal-card-foot">
                    <Button className={`mr-2 ${isProd ? 'is-danger' : 'is-warning'}`} onClick={onStartAnyway}>
                        Start Anyway
                    </Button>
                    <Button onClick={onClose}>Cancel</Button>
                </footer>
            </div>
        </div>
    );
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const ENV_STATUS_COLOR = { running: 'is-success', paused: 'is-warning', not_started: 'is-light', archived: 'is-dark' };

const humanizeStatus = s => s ? s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Unknown';

/**
 * Derive which approvals are pending for the current state's transition to 'running'.
 * Reviews prop shape: array of { reviewType, status } objects.
 * Returns an array of human-readable label strings for any that are not 'approved'.
 */
const getPendingApprovals = (currentState, reviews = []) => {
    const req = getTransitionRequirement(currentState, 'running');
    if (!req || req.kind !== 'reviews') return [];

    const approvedTypes = new Set(
        reviews.filter(r => r.status === 'approved').map(r => r.reviewType)
    );
    return req.reviews
        .filter(rt => !approvedTypes.has(rt))
        .map(rt => REVIEW_TYPES[rt]?.label ?? rt);
};

// ── Main component ───────────────────────────────────────────────────────────

const RunExperimentTab = ({ experimentKey, onStateChange, reviews = [], onNavigate }) => {
    const { execute } = useFetchWithMsal();
    const { editMode, addToast, checklistVersion } = useThor();

    const [expState, setExpState] = useState(buildLoadingState(null));
    const [transitionTarget, setTransitionTarget] = useState(null);
    const [startProdPending, setStartProdPending] = useState(false);
    const [isActing, setIsActing] = useState(false);

    // Fix 3 — bypass approval warning state
    const [showBypassWarning, setShowBypassWarning] = useState(false);
    const [bypassTarget, setBypassTarget] = useState(null); // 'dev' | 'prod'

    // Design-readiness validation, used to gate the design -> design_review transition.
    // Refetches on any successful save anywhere in the app (via checklistVersion), not just on mount/manual click.
    const [designValidation, setDesignValidation] = useState({ isValid: true, failedValidationMessages: [] });

    const fetchExperiment = useCallback(() => {
        setExpState(buildLoadingState(null));
        execute('GET', thor_api_urls.experiment(experimentKey))
            .then(data => {
                const detail = data?.experiment ?? data;
                setExpState(buildDataState(detail));
            })
            .catch(err => {
                console.error("Failed to fetch experiment:", err);
                setExpState(buildDataState(null));
            });
    }, [execute, experimentKey]);

    useEffect(() => { fetchExperiment(); }, [fetchExperiment]);

    useEffect(() => {
        execute('GET', thor_api_urls.validateExperiment(experimentKey))
            .then(data => setDesignValidation({
                isValid: data?.valid ?? data?.isValid ?? true,
                failedValidationMessages: data?.failedValidationMessages ?? [],
            }))
            .catch(() => setDesignValidation({ isValid: true, failedValidationMessages: [] }));
    }, [execute, experimentKey, checklistVersion]);

    const handleTransition = ({ targetState, transitionReason, isSkipValidations }) => {
        setIsActing(true);
        execute('PUT', thor_api_urls.experimentState(experimentKey), { targetState, transitionReason, isSkipValidations })
            .then(() => { setTransitionTarget(null); fetchExperiment(); addToast(`Moved to ${getStateLabel(targetState)}.`, 'success'); onStateChange?.(targetState); })
            .catch(err => addToast(serverErrMsg(err, 'Transition failed.'), 'danger'))
            .finally(() => setIsActing(false));
    };

    const handleEnvAction = (environment, action) => {
        setIsActing(true);
        const url = action === 'start' ? thor_api_urls.startExperiment(experimentKey) : thor_api_urls.pauseExperiment(experimentKey);
        execute('PUT', url, { environment })
            .then(() => { fetchExperiment(); addToast(`${environment} ${action === 'start' ? 'started' : 'paused'}.`, 'success'); })
            .catch(err => addToast(serverErrMsg(err, `Failed to ${action} ${environment}.`), 'danger'))
            .finally(() => setIsActing(false));
    };

    // Fix 3 — handle Start DEV/PROD clicks with approval gate
    const handleStartEnvClick = (env) => {
        const currentState = expState.data?.state;
        const pending = getPendingApprovals(currentState, reviews);

        if (pending.length > 0) {
            setBypassTarget(env.toLowerCase());
            setShowBypassWarning(true);
        } else if (env === 'PROD') {
            setStartProdPending(true);
        } else {
            handleEnvAction(env, 'start');
        }
    };

    // After bypass warning: user clicked "Start Anyway"
    const handleBypassConfirm = () => {
        setShowBypassWarning(false);
        if (bypassTarget === 'prod') {
            setStartProdPending(true);
        } else {
            handleEnvAction('DEV', 'start');
        }
        setBypassTarget(null);
    };

    const detail = expState.data;
    const currentState = detail?.state;
    const validTransitions = getValidTransitions(currentState);
    // Fix 2 — compute progress index against main path only
    const mainPathIndex = LIFECYCLE_MAIN_PATH.indexOf(currentState);

    // Fix 3 — pending approvals for bypass warning display
    const pendingApprovals = getPendingApprovals(currentState, reviews);

    return (
        <StateHandler showLoader={expState.loading} showError={false} loadingScreen={<SmallLoadingSpinner />}>
            <p className="subtitle is-5 mb-4">Run Experiment</p>

            {/* Current state */}
            <div className="box mb-4">
                <p className="has-text-grey is-size-7 mb-1">Current State</p>
                <StateChip state={currentState} className="is-medium" />
            </div>

            {/* Live-bucketing pointer — surfaced once the experiment has traffic */}
            {onNavigate && ['running', 'paused', 'ended', 'cleanup', 'finished'].includes(currentState) && (
                <div className="notification is-info is-light py-3 mb-4 is-flex is-align-items-center is-justify-content-space-between">
                    <span>❄️ This experiment is live — see who's being bucketed and why in <strong>Bucket Decisions</strong>.</span>
                    <button className="button is-info is-small" onClick={() => onNavigate('bucketDecisions')}>
                        See live bucketing →
                    </button>
                </div>
            )}

            {/* Fix 2 — Lifecycle progress (main path only, side-branch indicators below) */}
            <div className="mb-4">
                <p className="label is-small mb-2">Lifecycle</p>
                <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    {LIFECYCLE_MAIN_PATH.map((s, i) => (
                        <span key={s} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <span className={`tag is-small ${s === currentState ? 'is-link' : (mainPathIndex >= 0 && i < mainPathIndex) ? 'is-success is-light' : 'is-light has-text-grey'}`}>
                                {getStateLabel(s)}
                            </span>
                            {i < LIFECYCLE_MAIN_PATH.length - 1 && (
                                <span className="has-text-grey">›</span>
                            )}
                        </span>
                    ))}
                </div>
                {/* Side-branch indicators */}
                {currentState === 'paused' && (
                    <p className="help has-text-warning-dark mt-1" style={{ fontSize: '0.78rem' }}>
                        ⏸ Currently paused — resume to continue
                    </p>
                )}
                {currentState === 'cancelled' && (
                    <p className="help has-text-danger mt-1" style={{ fontSize: '0.78rem' }}>
                        ✕ Experiment was cancelled
                    </p>
                )}
            </div>

            {/* Next Steps callout */}
            {currentState && !['finished', 'cancelled'].includes(currentState) && (
                <div className="notification is-info is-light mb-4" style={{ fontSize: '0.875rem' }}>
                    <p className="has-text-weight-semibold mb-1">What's next?</p>
                    {currentState === 'design' && <p>Complete <strong>Hypothesis</strong>, <strong>Buckets</strong>, <strong>Metrics</strong>, and <strong>Audiences</strong> setup, then transition to <strong>Design Review</strong> when ready.</p>}
                    {currentState === 'design_review' && <p>The <strong>Analyst Design Review</strong> and <strong>Engineer Design Review</strong> approvals need to be completed (see Approvals tab).</p>}
                    {currentState === 'instrumentation' && <p>Engineer must complete instrumentation setup and submit the <strong>Engineer Instrumentation Signoff</strong> approval.</p>}
                    {currentState === 'pre_prod_validation' && <p>The experiment owner must submit the <strong>Owner Pre-prod Signoff</strong> approval (see Approvals tab), then transition to <strong>Running</strong>.</p>}
                    {/* Fix 6 — improved running state message */}
                    {currentState === 'running' && (() => {
                        const startDate = detail?.startDate ?? detail?.targetedStartDate;
                        const daysElapsed = startDate ? Math.floor((Date.now() - new Date(startDate)) / 86400000) : null;
                        const daysRemaining = daysElapsed != null && detail?.daysToPower ? Math.max(0, detail.daysToPower - daysElapsed) : null;
                        return (
                            <>
                                <p>Your experiment is live! Monitor metric results in Heimdall. When the experiment has run long enough, end it and record the outcome.</p>
                                {daysRemaining !== null && (
                                    <div className="mt-2" style={{ fontSize: '0.8rem' }}>
                                        <span style={{ color: daysRemaining === 0 ? '#257942' : '#555' }}>
                                            {daysRemaining === 0
                                                ? '✓ Experiment has reached its power target — review results and decide.'
                                                : `⏱ ${daysElapsed} days elapsed · ${daysRemaining} days remaining until power target (${detail.daysToPower} days total)`}
                                        </span>
                                    </div>
                                )}
                            </>
                        );
                    })()}
                    {currentState === 'paused' && <p>Experiment is paused. Resume by starting DEV/PROD again, or transition to <strong>Test Ending and Decision</strong>.</p>}
                    {/* Fix 1 — corrected state key: 'ended' not 'test_ending_and_decision' */}
                    {currentState === 'ended' && <p>Record the experiment <strong>Outcome</strong> (see Outcome tab), then transition to <strong>Clean Up</strong>.</p>}
                    {currentState === 'cleanup' && <p>Engineer must submit the <strong>Engineer Cleanup Signoff</strong> approval to complete this experiment.</p>}
                </div>
            )}

            {/* Dev / Prod environment controls */}
            {(detail?.devStatus || detail?.prodStatus) && (
                <div className="columns mb-4">
                    {['DEV', 'PROD'].map(env => {
                        const status = env === 'DEV' ? detail.devStatus : detail.prodStatus;
                        const isRunning = status === 'running';
                        return (
                            <div className="column is-half" key={env}>
                                <div className="box">
                                    <div className="level mb-2">
                                        <div className="level-left">
                                            <span className="has-text-weight-semibold mr-2">{env}</span>
                                            <span className={`tag ${ENV_STATUS_COLOR[status] ?? 'is-light'}`}>{humanizeStatus(status)}</span>
                                        </div>
                                    </div>
                                    <div className="buttons">
                                        {!isRunning && (
                                            <Button className="is-small is-success"
                                                    disabled={!editMode || isActing}
                                                    onClick={() => handleStartEnvClick(env)}>
                                                Start {env}
                                            </Button>
                                        )}
                                        {isRunning && (
                                            <Button className="is-small is-warning"
                                                    disabled={!editMode || isActing}
                                                    onClick={() => handleEnvAction(env, 'pause')}>
                                                Pause {env}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Fix 5 — State transitions with title tooltips */}
            {validTransitions.length > 0 && (
                <div>
                    <p className="label is-small mb-2">Transitions</p>
                    <div className="buttons">
                        {validTransitions.map(target => {
                            const tooltipKey = `${currentState}->${target}`;
                            const isDesignToReview = currentState === 'design' && target === 'design_review';
                            const blockedByValidation = isDesignToReview && !designValidation.isValid;
                            const tooltip = blockedByValidation
                                ? 'Experiment must pass validation before moving to Design Review — see issues below.'
                                : TRANSITION_TOOLTIPS[tooltipKey];
                            return (
                                <Button key={target}
                                        className={`is-small ${target === 'cancelled' ? 'is-danger is-light' : 'is-link is-light'}`}
                                        disabled={!editMode || isActing || blockedByValidation}
                                        title={tooltip}
                                        onClick={() => setTransitionTarget(target)}>
                                    → {getStateLabel(target)}
                                </Button>
                            );
                        })}
                    </div>
                    {!editMode && <p className="help">Enable Edit mode to make transitions.</p>}
                    {currentState === 'design' && !designValidation.isValid && designValidation.failedValidationMessages.length > 0 && (
                        <div className="notification is-warning is-light mt-2 mb-0" style={{ padding: '0.75rem' }}>
                            <p className="has-text-weight-semibold is-size-7 mb-2">
                                Fix these before moving to Design Review:
                            </p>
                            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                                {designValidation.failedValidationMessages.map((msg, i) => {
                                    const humanMsg = humanizeMessage(msg);
                                    const dest = onNavigate ? getTabForMessage(msg) : null;
                                    return (
                                        <li key={i} className="is-size-7 mb-1"
                                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.5rem' }}>
                                            <span>&bull; {humanMsg}</span>
                                            {dest && (
                                                <a style={{ whiteSpace: 'nowrap', flexShrink: 0, color: '#3273dc', textDecoration: 'none', cursor: 'pointer' }}
                                                   onClick={(e) => { e.preventDefault(); onNavigate(dest.tab); }}
                                                   href="#">
                                                    → Fix in {dest.label}
                                                </a>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {validTransitions.length === 0 && currentState && (
                <p className="has-text-grey is-size-7">This experiment is in a terminal state — no further transitions available.</p>
            )}

            {/* Fix 3 — Bypass approval warning modal */}
            <BypassApprovalModal
                isActive={showBypassWarning}
                isProd={bypassTarget === 'prod'}
                pendingApprovals={pendingApprovals}
                onClose={() => { setShowBypassWarning(false); setBypassTarget(null); }}
                onStartAnyway={handleBypassConfirm}
            />

            <StartProdModal isActive={startProdPending} experimentKey={experimentKey}
                            isSubmitting={isActing}
                            onClose={() => setStartProdPending(false)}
                            onConfirm={() => { setStartProdPending(false); handleEnvAction('PROD', 'start'); }} />

            <TransitionModal isActive={!!transitionTarget} targetState={transitionTarget}
                             isSubmitting={isActing}
                             onClose={() => setTransitionTarget(null)} onConfirm={handleTransition} />
        </StateHandler>
    );
};

export default RunExperimentTab;
